import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMessage } from "@/lib/api";
import { CrudSection, YesNo } from "@/components/admin/CrudSection";
import { DrawPreview } from "@/components/admin/DrawPreview";

const ORDER = { name: "order", label: "Ordre", type: "number" };
const ACTIVE = { name: "active", label: "Actif", type: "bool" };
const SIMPLE = [{ name: "text", label: "Question", type: "text" }, { name: "simple_safe_answer", label: "Réponse sûre", type: "yesno" },
  { name: "negative_text", label: "Formulation inverse (facultative, utilisée seulement si vous activez la formulation variable)", type: "text" }, ORDER, ACTIVE];
const VARY = [{ name: "text", label: "Formulation principale", type: "text" },
  { name: "simple_safe_answer", label: "Réponse sûre de la formulation principale", type: "yesno" },
  { name: "negative_text", label: "Formulation inverse (réponse sûre opposée, automatique)", type: "text" }, ORDER, ACTIVE];
const inv = (a) => (a === "non" ? "OUI" : "NON");

const MissingAnswers = ({ missing, onDone, onCancel }) => {
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/take5-settings", { vary_wording: false, simple_answers: ans }); onDone(); } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="take5-missing-answers" className="mt-3 space-y-3 rounded-md border border-amber-500/60 p-3">
      {missing.map((q) => (
        <div key={q.id}><p className="text-sm font-semibold">{q.text}</p>
          <p className="text-xs text-muted-foreground">Réponse sûre lorsque cette question est utilisée seule :</p>
          <YesNo value={ans[q.id]} onChange={(v) => setAns({ ...ans, [q.id]: v })} testId={`missing-answer-${q.id}`} /></div>
      ))}
      <div className="flex gap-2">
        <Button data-testid="take5-missing-save" disabled={busy || missing.some((q) => !ans[q.id])} onClick={save}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Valider et passer en mode simple</Button>
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
};

const ModeToggle = ({ vary, onSaved }) => {
  const [problem, setProblem] = useState(null);
  const toggle = async (on) => {
    setProblem(null);
    try { await api.put("/admin/take5-settings", { vary_wording: on }); toast.success("Réglage enregistré."); onSaved(); } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.incomplete || d?.missing) setProblem(d); else toast.error(errMessage(e));
    }
  };
  return (
    <div className="mb-5 rounded-lg border border-border bg-card p-4" data-testid="take5-mode-setting">
      <label className="flex min-h-[44px] items-center gap-3 font-semibold">
        <input type="checkbox" data-testid="take5-vary-wording" className="h-5 w-5 accent-amber-500" checked={vary} onChange={(e) => toggle(e.target.checked)} /> Varier la formulation des questions
      </label>
      <p className="text-sm text-muted-foreground">Alterner entre formulations positives et négatives afin d'encourager la lecture de chaque question.</p>
      {problem?.incomplete && (
        <div role="alert" data-testid="take5-incomplete-questions" className="mt-3 rounded-md border border-red-500/60 bg-red-500/10 p-3 text-sm">
          <p className="font-semibold">{problem.message}</p>
          <ul className="mt-1 list-disc pl-5">{problem.incomplete.map((q) => <li key={q.id}>{q.text}</li>)}</ul>
        </div>
      )}
      {problem?.missing && <MissingAnswers missing={problem.missing} onDone={() => { setProblem(null); toast.success("Mode simple activé."); onSaved(); }} onCancel={() => setProblem(null)} />}
    </div>
  );
};

export const QuestionsPanel = ({ cfg, options, onChanged }) => {
  const [vary, setVary] = useState(null);
  const [rev, setRev] = useState(0);
  const load = () => api.get("/admin/take5-settings").then((r) => setVary(r.data.vary_wording)).catch(() => setVary(false));
  useEffect(() => { load(); }, []);
  if (vary === null) return null;
  const safe = (r) => (r.simple_safe_answer ? r.simple_safe_answer.toUpperCase() : "à définir");
  const sub = (r) => (vary ? `Réponse sûre : ${safe(r)} · ${r.negative_text ? `Inverse (${inv(r.simple_safe_answer)}) : ${r.negative_text}` : "Formulation inverse à compléter"}`
    : `Réponse sûre : ${safe(r)}`);
  const modeCfg = { ...cfg, fields: vary ? VARY : SIMPLE, subtitle: sub, defaults: { order: 10, active: true },
    help: vary ? "Mode formulation variable : chaque Take 5 affiche, pour chaque question, la formulation principale (sa réponse sûre) ou la formulation inverse (réponse sûre opposée)."
      : "Mode simple : une formulation par question ; la tâche n'est autorisée que si chaque réponse correspond à la réponse sûre définie." };
  return (
    <>
      <ModeToggle vary={vary} onSaved={() => { load(); setRev((r) => r + 1); onChanged?.(); }} />
      <DrawPreview vary={vary} refreshKey={rev} />
      <CrudSection key={String(vary)} cfg={modeCfg} options={options} onChanged={() => { setRev((r) => r + 1); onChanged?.(); }} />
    </>
  );
};
